import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { GenreContrat, Langue, EtatContrat } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { Pagination } from './types.js';
import type { ReponseDocumentSigne } from './documentsGalerie.js';
export interface ArticleContrat {
  titre: string;
  texte: string;
}
export interface CorpsModeleContrat {
  titre: string;
  preambule: string;
  articles: ArticleContrat[];
  fait: string;
  valideParGalerie: boolean;
}
export interface ModeleContratPresente extends CorpsModeleContrat {
  genre: GenreContrat;
  langue: Langue;
  version: number;
  champs: readonly string[];
  modifieLe: string;
}
export interface ContratPresente {
  id: string;
  numero: string;
  genre: GenreContrat;
  langue: Langue;
  titre: string;
  etat: EtatContrat;
  artisteId: string | null;
  contactId: string | null;
  oeuvres: string[];
  emisLe: string;
  signeLe: string | null;
  annuleLe: string | null;
  mentionValidation: boolean;
  modeleVersion: number;
  documentId: string | null;
  preambule: string;
  articles: ArticleContrat[];
  fait: string;
}
export interface CorpsGeneration {
  genre: GenreContrat;
  langue?: Langue;
  artisteId?: string;
  contactId?: string;
  oeuvres?: string[];
  saisies?: Record<string, string>;
  date?: string;
}
export interface ApercuContrat {
  titre: string;
  preambule: string;
  articles: ArticleContrat[];
  fait: string;
  valeurs: Record<string, string>;
  manquants: string[];
}
export interface FiltresContrats {
  genre?: GenreContrat;
  artisteId?: string;
  contactId?: string;
  page?: number;
  parPage?: number;
}
const racine = (g: string) => `/g/${encodeURIComponent(g)}/modeles-contrats`;
const modele = (g: string, genre: GenreContrat, langue: Langue) =>
  `${racine(g)}/modeles/${genre}/${langue}`;
const fiche = (g: string, id: string) => `${racine(g)}/contrats/${encodeURIComponent(id)}`;
function params(f: FiltresContrats): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined) p.set(k, String(v));
  return p.toString();
}
export const contratsApi = {
  modeles: (g: string, a?: AppelApi) =>
    api.get<{ modeles: ModeleContratPresente[] }>(`${racine(g)}/modeles`, { galerie: g, ...a }),
  modele: (g: string, genre: GenreContrat, langue: Langue, a?: AppelApi) =>
    api.get<{ modele: ModeleContratPresente }>(modele(g, genre, langue), { galerie: g, ...a }),
  enregistrerModele: (
    g: string,
    genre: GenreContrat,
    langue: Langue,
    corps: CorpsModeleContrat,
    a?: AppelApi,
  ) =>
    api.put<{ modele: ModeleContratPresente }>(modele(g, genre, langue), corps, {
      galerie: g,
      ...a,
    }),
  reinitialiserModele: (g: string, genre: GenreContrat, langue: Langue, a?: AppelApi) =>
    api.post<{ modele: ModeleContratPresente }>(
      `${modele(g, genre, langue)}/reinitialiser`,
      {},
      { galerie: g, ...a },
    ),
  apercu: (g: string, corps: CorpsGeneration, a?: AppelApi) =>
    api.post<ApercuContrat>(`${modele(g, corps.genre, corps.langue ?? 'fr')}/apercu`, corps, {
      galerie: g,
      ...a,
    }),
  generer: (g: string, corps: CorpsGeneration, a?: AppelApi) =>
    api.post<{ contrat: ContratPresente; document: ReponseDocumentSigne }>(
      `${racine(g)}/contrats`,
      corps,
      { galerie: g, ...a },
    ),
  lister: (g: string, f: FiltresContrats = {}, a?: AppelApi) =>
    api.get<{ contrats: ContratPresente[]; pagination: Pagination }>(
      `${racine(g)}/contrats?${params(f)}`,
      { galerie: g, ...a },
    ),
  obtenir: (g: string, id: string, a?: AppelApi) =>
    api.get<{ contrat: ContratPresente }>(fiche(g, id), { galerie: g, ...a }),
  pdf: (g: string, id: string, a?: AppelApi) =>
    api.get<ReponseDocumentSigne>(`${fiche(g, id)}/pdf`, { galerie: g, ...a }),
  signer: (g: string, id: string, signeLe?: string, a?: AppelApi) =>
    api.post<{ contrat: ContratPresente }>(`${fiche(g, id)}/signe`, signeLe ? { signeLe } : {}, {
      galerie: g,
      ...a,
    }),
  annuler: (g: string, id: string, motif: string, a?: AppelApi) =>
    api.post<{ contrat: ContratPresente }>(
      `${fiche(g, id)}/annuler`,
      { motif },
      { galerie: g, ...a },
    ),
  versCoffre: (g: string, id: string, a?: AppelApi) =>
    api.post<{ documentId: string }>(`${fiche(g, id)}/coffre`, {}, { galerie: g, ...a }),
};
export function useModelesContrats(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'contrats', 'modeles'),
    queryFn: (c) => contratsApi.modeles(g, appelDeRequete(c)),
    select: (r) => r.modeles,
  });
}
export function useModeleContrat(g: string, genre: GenreContrat, langue: Langue) {
  return useQuery({
    queryKey: cleMetier(g, 'contrats', 'modele', genre, langue),
    queryFn: (c) => contratsApi.modele(g, genre, langue, appelDeRequete(c)),
    select: (r) => r.modele,
  });
}
export function useContrats(g: string, f: FiltresContrats = {}, enabled = true) {
  return useQuery({
    queryKey: cleMetier(g, 'contrats', 'liste', f),
    queryFn: (c) => contratsApi.lister(g, f, appelDeRequete(c)),
    enabled,
  });
}
export function useContrat(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'contrats', 'fiche', id),
    queryFn: (c) => contratsApi.obtenir(g, id, appelDeRequete(c)),
    select: (r) => r.contrat,
  });
}
export function useEnregistrerModeleContrat(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { genre: GenreContrat; langue: Langue; corps: CorpsModeleContrat }, a) =>
        contratsApi.enregistrerModele(g, v.genre, v.langue, v.corps, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'contrats') });
      },
    }),
  );
}
export function useReinitialiserModeleContrat(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { genre: GenreContrat; langue: Langue }, a) =>
        contratsApi.reinitialiserModele(g, v.genre, v.langue, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'contrats') });
      },
    }),
  );
}
export function useOuvrirContratPdf(g: string) {
  return useMutation(dansLaPortee({ mutationFn: (id: string, a) => contratsApi.pdf(g, id, a) }));
}
export function useApercuContrat(g: string) {
  return useMutation(
    dansLaPortee({ mutationFn: (v: CorpsGeneration, a) => contratsApi.apercu(g, v, a) }),
  );
}
export function useGenererContrat(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: CorpsGeneration, a) => contratsApi.generer(g, v, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'contrats') });
      },
    }),
  );
}
export function useSignerContrat(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; signeLe?: string }, a) =>
        contratsApi.signer(g, v.id, v.signeLe, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'contrats') });
      },
    }),
  );
}
export function useAnnulerContrat(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (v: { id: string; motif: string }, a) => contratsApi.annuler(g, v.id, v.motif, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'contrats') });
      },
    }),
  );
}
export function useContratVersCoffre(g: string) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, a) => contratsApi.versCoffre(g, id, a),
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'contrats') });
        void q.invalidateQueries({ queryKey: cleMetier(g, 'coffre') });
      },
    }),
  );
}
